import { useEffect, useState } from "react";
import Ripples from "react-ripples";

const Pagination = ({
  paginationLinks = [],
  listMeta = { current_page: 1, last_page: 1 },
  hostUrl = "",
  setRequestParams = () => {},
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 780);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getTrimmedLink = (url) =>
    url?.split(hostUrl.replace("http://", "").replace("https://", ""))[1];

  const currentPage = listMeta.current_page;
  const totalPages = listMeta.last_page;

  const renderPageButton = (label, active = false, url = null) => {
    const trimmedLink = getTrimmedLink(url);
    return (
      <Ripples
        key={label}
        color="#ffffff40"
        during={600}
        className="rounded-lg"
      >
        <div
          className={`pagination-bg ${active ? "active" : "hasHover"}`}
          onClick={() => {
            if (url && !active) setRequestParams(trimmedLink);
          }}
        >
          <span className="font-inter text-sm font-bold">{label}</span>
        </div>
      </Ripples>
    );
  };

  const renderEllipsis = (key) => (
    <div
      key={key}
      className="pagination-bg select-none px-3 py-2 rounded-lg text-blue-600 font-bold"
    >
      ...
    </div>
  );

  const prevLink = paginationLinks.find((l) => l.label === "&laquo; Previous");
  const nextLink = paginationLinks.find((l) => l.label === "Next &raquo;");

  const mappedPagination = [];

  // ⬅ Previous
  if (prevLink && currentPage > 1) {
    mappedPagination.push(
      <Ripples key="prev" color="#ffffff40" during={600} className="rounded-lg">
        <div
          className="pagination-bg hasHover"
          onClick={() => setRequestParams(getTrimmedLink(prevLink.url))}
        >
          ⬅
        </div>
      </Ripples>
    );
  }

  // Always show first page
  const firstPage = paginationLinks.find((l) => l.label === "1");
  if (firstPage)
    mappedPagination.push(
      renderPageButton("1", currentPage === 1, firstPage.url)
    );

  // Determine middle range
  let start = Math.max(currentPage - 2, 2);
  let end = Math.min(currentPage + 2, totalPages - 1);

  if (start > 2) mappedPagination.push(renderEllipsis("left-dots"));

  for (let i = start; i <= end; i++) {
    const link = paginationLinks.find((l) => l.label === i.toString());
    if (link)
      mappedPagination.push(
        renderPageButton(i.toString(), currentPage === i, link.url)
      );
  }

  if (end < totalPages - 1) mappedPagination.push(renderEllipsis("right-dots"));

  if (totalPages > 1) {
    const lastPage = paginationLinks.find(
      (l) => l.label === totalPages.toString()
    );
    if (lastPage)
      mappedPagination.push(
        renderPageButton(
          totalPages.toString(),
          currentPage === totalPages,
          lastPage.url
        )
      );
  }

  // ⮕ Next
  if (nextLink && currentPage < totalPages) {
    mappedPagination.push(
      <Ripples key="next" color="#ffffff40" during={600} className="rounded-lg">
        <div
          className="pagination-bg hasHover"
          onClick={() => setRequestParams(getTrimmedLink(nextLink.url))}
        >
          ⮕
        </div>
      </Ripples>
    );
  }

  // Short version for mobile
  const shortPagination = [];

  if (currentPage > 1 && prevLink) {
    shortPagination.push(
      <Ripples key="prev" color="#ffffff40" during={600} className="rounded-lg">
        <div
          className="pagination-bg hasHover"
          onClick={() => setRequestParams(getTrimmedLink(prevLink.url))}
        >
          ⬅
        </div>
      </Ripples>
    );
  }

  shortPagination.push(
    <div
      key="current"
      className="pagination-bg active select-none font-bold text-base"
    >
      {currentPage}
    </div>
  );

  if (currentPage < totalPages && nextLink) {
    shortPagination.push(
      <Ripples key="next" color="#ffffff40" during={600} className="rounded-lg">
        <div
          className="pagination-bg hasHover"
          onClick={() => setRequestParams(getTrimmedLink(nextLink.url))}
        >
          ⮕
        </div>
      </Ripples>
    );
  }

  return (
    <div className="flex justify-center items-center gap-1 px-2 overflow-x-auto">
      {isMobile ? shortPagination : mappedPagination}
    </div>
  );
};

export default Pagination;
